/**
 * API service configuration for VentureOS
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Direct origin for endpoints that must bypass the Netlify /api/* proxy.
// Netlify's redirect proxy enforces a ~6 MB request-body cap, which kills
// multipart pitch-deck uploads above that threshold. This URL points
// straight at the Railway origin so those uploads skip the proxy entirely.
// Falls back to the shared API_BASE_URL when unset (dev / preview builds
// where REACT_APP_API_URL already points at a non-proxied origin).
const API_DIRECT_URL =
  process.env.REACT_APP_API_DIRECT_URL || API_BASE_URL;

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Separate axios instance that targets the Railway origin directly,
// bypassing the Netlify proxy (which caps request bodies at ~6 MB).
// Use this for large multipart uploads (e.g. pitch decks). It mirrors
// the auth behavior of the shared `api` instance: the request
// interceptor below reads the same `vc_research_token` localStorage
// key that auth.ts writes. We deliberately give this instance a longer
// timeout because uploads + Claude Vision analysis can run 30-60s.
const apiDirect = axios.create({
  baseURL: API_DIRECT_URL,
  timeout: 120000, // 2 min — uploads + analysis can be slow
});

// Attach the bearer token to apiDirect requests. We don't import
// authService here to avoid a circular dependency (authService imports
// from this file). Reading localStorage directly with the same key is
// the simplest mirror.
apiDirect.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem('vc_research_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // localStorage unavailable (SSR / private mode) — skip silently
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: normalize FastAPI validation errors (detail can
// be an array of {type, loc, msg, …}) into a human-readable string, and
// synthesize a non-empty detail when none was supplied so callers don't
// fall back to a generic "Upload failed" toast that hides the cause.
// Mirrors the `api` instance's response interceptor (which apiDirect
// previously skipped, leading to opaque pitch-deck-upload errors).
apiDirect.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      if (
        error.response?.data?.detail &&
        Array.isArray(error.response.data.detail)
      ) {
        const messages = error.response.data.detail.map((e: any) => {
          const loc = Array.isArray(e.loc)
            ? e.loc.filter((p: any) => p !== 'query' && p !== 'body').join('.')
            : '';
          return loc ? `${loc}: ${e.msg}` : e.msg;
        });
        error.response.data.detail = messages.join('; ');
      }
      // If the backend returned no detail at all, synthesize one so the
      // toast carries the HTTP status / network state instead of a bare
      // fallback string.
      if (error.response && !error.response.data?.detail) {
        const status = error.response.status;
        const statusText = error.response.statusText || '';
        error.response.data = {
          ...(error.response.data || {}),
          detail: `Server returned ${status}${statusText ? ` ${statusText}` : ''} with no detail.`,
        };
      } else if (!error.response && error.request) {
        // No response received — typically CORS, DNS, or proxy issue.
        error.response = {
          data: {
            detail:
              'Network error: no response from server. Check that the upload origin is reachable (CORS / proxy / VPN).',
          },
        };
      }
      // Surface in console so the dev / operator can grab it from devtools.
      // eslint-disable-next-line no-console
      console.error(
        '[apiDirect] error',
        error.response?.status,
        error.response?.data || error.message,
      );
    } catch {
      // Defensive — never let the interceptor itself throw.
    }
    return Promise.reject(error);
  },
);

export { apiDirect };

// Attach the bearer token on every request. Mirrors the apiDirect
// interceptor — reads localStorage directly to avoid a circular import
// on authService. Previously the auth header was only attached after
// authService.setupInterceptors() ran, and that only fired on session
// restore at app boot — fresh sign-ins (verifyCode -> isAuthenticated)
// never installed the interceptor, so any auth-gated endpoint
// (e.g. /ai/consensus, /team) failed with "header Authorization
// field required" until the user reloaded the page.
api.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem('vc_research_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // localStorage unavailable (SSR / private mode) — skip silently.
    }
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Log API responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, response.status);
    }
    return response;
  },
  (error) => {
    // Normalize FastAPI validation errors (detail can be an array of
    // {type, loc, msg, input, ctx}) into a human-readable string so
    // downstream code can safely render it.
    if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
      const messages = error.response.data.detail.map((e: any) => {
        const loc = Array.isArray(e.loc) ? e.loc.filter((p: any) => p !== 'query' && p !== 'body').join('.') : '';
        return loc ? `${loc}: ${e.msg}` : e.msg;
      });
      error.response.data.detail = messages.join('; ');
    }

    // Enhanced error handling
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      console.error(`❌ API Error ${status}:`, data?.message || error.message);

      // Handle specific error codes
      switch (status) {
        case 401:
          console.warn('Unauthorized access - token may be expired');
          // Auto-recover from stale tokens: when the backend rejects
          // our token (legacy opaque token after a deploy wiped the
          // in-memory map, or a JWT signed with an old SECRET_KEY),
          // wipe credentials and bounce to /signin so the user gets a
          // fresh one without having to manually clear localStorage.
          // Skip if we're already on /signin or if this IS the auth
          // refresh call to avoid a redirect loop.
          try {
            const url = String(error.config?.url || '');
            const onSigninPage = window.location.pathname.startsWith('/signin');
            const isAuthCall = url.includes('/auth/refresh') || url.includes('/auth/signin');
            if (!onSigninPage && !isAuthCall) {
              localStorage.removeItem('vc_research_token');
              localStorage.removeItem('vc_research_refresh_token');
              localStorage.removeItem('vc_research_user');
              window.location.href = '/signin';
            }
          } catch {
            // localStorage / window unavailable — let the caller surface the 401.
          }
          break;
        case 403:
          console.warn('Access forbidden - insufficient permissions');
          break;
        case 404:
          console.warn('Resource not found');
          break;
        case 429:
          console.warn('Rate limit exceeded');
          break;
        case 500:
          console.error('Server error - please try again later');
          break;
        default:
          console.error(`Unexpected error: ${status}`);
      }
    } else if (error.request) {
      // Network error
      console.error('Network Error: No response received');
    } else {
      console.error('Request Error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default api;